/** @jsxImportSource @emotion/react */
import { jsx, css } from "@emotion/react";
import "./i18n/config";
import { GroupMember, GroupMemberInfo, type GroupInfo } from "./types/gigya";
import { REDIRECT_URLS } from "./constants";
import { useState, createContext, useEffect, SetStateAction } from "react";
import {
  useGetAllMemberGroups,
  useGetGroupMemberInfo,
  useSearchClinics,
  useSetMemberInfo,
  useRemoveMember,
  useSetGroupMemberInfo,
  useSetGroupInfo,
} from "./api";
import {
  TableMember,
  TableMembers,
  TableMembersPending,
  ModalNickname,
  ModalNewMember,
  ModalRemoveMember,
  Loading,
  ModalLeaveTeam,
  LanguageSelector,
  ErrorIcon,
} from "./components";
import { GigyaGroupModels } from "./types/gigya.schema";
import { Button, Tab, Tabs } from "@mui/material";
import { useMobileDetector } from "./hooks";
import { SelectW } from "./muiConverter";
import { useTranslation } from "react-i18next";
import { Empty } from "./components/Empty";

const ENVIRONMENT: string = process.env.REACT_APP_ENVIRONMENT || "dev";

const getRedirectUrl = () => {
  const TST = "tst";
  const PRD = "prd";

  // SVSE
  if (process.env.REACT_APP_LOCALE === "sv-se") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.SE.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.SE.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.SE.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // NNNO
  if (process.env.REACT_APP_LOCALE === "nn-no") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.NO.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.NO.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.NO.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // DADK
  if (process.env.REACT_APP_LOCALE === "da-dk") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.DK.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.DK.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.DK.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // ITIT
  if (process.env.REACT_APP_LOCALE === "it-it") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.IT.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.IT.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.IT.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // NLNL
  if (process.env.REACT_APP_LOCALE === "nl-nl") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.NL.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.NL.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.NL.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // DEAT
  if (process.env.REACT_APP_LOCALE === "de-at") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.AT.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.AT.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.AT.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // DECH
  if (process.env.REACT_APP_LOCALE === "de-ch") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.DE_CH.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.DE_CH.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.DE_CH.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // FRCH
  if (process.env.REACT_APP_LOCALE === "fr-ch") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.FR_CH.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.FR_CH.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.FR_CH.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // DEDE
  if (process.env.REACT_APP_LOCALE === "de-de") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.DE.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.DE.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.DE.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // ZHTW
  if (process.env.REACT_APP_LOCALE === "zh-tw") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.TW.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.TW.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.TW.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // KOKR
  if (process.env.REACT_APP_LOCALE === "ko-kr") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.KR.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.KR.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.KR.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // JAJP
  if (process.env.REACT_APP_LOCALE === "ja-jp") {
    if (ENVIRONMENT === TST)
      return REDIRECT_URLS.JP.REACT_APP_TST_REDIRECT_URL || "";
    if (ENVIRONMENT === PRD)
      return REDIRECT_URLS.JP.REACT_APP_PRD_REDIRECT_URL || "";
    return REDIRECT_URLS.JP.REACT_APP_DEV_REDIRECT_URL || "";
  }

  // ENUS
  if (ENVIRONMENT === TST)
    return REDIRECT_URLS.US.REACT_APP_TST_REDIRECT_URL || "";
  if (ENVIRONMENT === PRD)
    return REDIRECT_URLS.US.REACT_APP_PRD_REDIRECT_URL || "";
  return REDIRECT_URLS.US.REACT_APP_DEV_REDIRECT_URL || "";
};

const UserContext = createContext({});

type SEARCHGROUP = {
  UID: string;
  groupId: string;
  model: string;
  relationshipData: {
    role: string;
    status: string;
  };
  profile: {
    lastName: string;
    country: string;
    locale: string;
    email: string;
    zip: string;
    firstName: string;
  };
  memberSince: string;
  lastUpdated: string;
  memberSinceTimestamp: number;
  lastUpdatedTimestamp: number;
  permissions: string;
};

type SEARCHGROUPINFO = {
  groupId: string;
  model: string;
  groupData: {
    clinicName: string;
    priceGroup: string;
    country: string;
    city: string;
    SFHomeDeliveryOnly: boolean;
    industry: string;
    salesOrg: string;
    altID: string;
    payer: string;
    archived: boolean;
    salesZone: string;
    clinicType: string;
    customerClass: string;
    billTo: string;
    softClosed: boolean;
    state: string;
    billingZip: string;
    zip: string;
    companyCode: string;
    soldToClinicName: string;
    soldTo: string;
    customerGroup: string;
    customerPlanGroup: string;
    salesTerritory: string;
    accountType: string;
    approvedEmails: string;
    streetAddress: string;
    blockedClinic: boolean;
    paymentTerm: string;
    billingCity: string;
    shipTo: string;
    salesRegion: string;
    customerPriceProcedure: string;
    removedUsers?: string | string[];
  };
  created: string;
  lastUpdated: string;
  createdTimestamp: number;
  lastUpdatedTimestamp: number;
  membersCount: number;
};

type WithClinic = {
  groupId: string;
};

type GroupMemberInfoWithClinic = GroupMemberInfo & WithClinic;

export default function App() {
  const { t } = useTranslation();
  window.gigya
    .hasSession()
    .then((sessionExist) => {
      if (!sessionExist) {
        window.location.href = getRedirectUrl();
      }
    })
    .catch((err) => {
      console.log(err);
    });

  const { isMobile } = useMobileDetector();
  const {
    memberGroups,
    setMemberGroups,
    loadingMemberGroups,
    memberGroupsError,
  } = useGetAllMemberGroups();
  const {
    groupMemberInfo,
    setGroupMemberInfo,
    setNewGroupMemberInfoID,
    loadingGroupMemberInfo,
    groupMemberInfoError,
  } = useGetGroupMemberInfo(memberGroups[0]?.groupId);
  const {
    removeMemberResp,
    setRemovedMemberInfo,
    loadingRemoveMember,
    removeMemberError,
  } = useRemoveMember(groupMemberInfo?.UID, memberGroups[0]?.groupId);

  const [pendingMembers, setPendingMembers] = useState<GroupMemberInfo[]>([]);
  const [resolvedMembers, setResolvedMembers] = useState<GroupMemberInfo[]>([]);
  const [updatedMembers, setUpdatedMembers] = useState<
    GroupMemberInfoWithClinic[]
  >([]);
  const [isChangesMade, setIsChangesMade] = useState(false);

  // This is an Asian only API Hook for now
  // searchClinicsInfoResp is just for clinicName
  const {
    searchClinicsResp,
    setSearchClinicsResp,
    searchClinicsInfoResp,
    setGroupId,
    loadingSearchClinicsInfoResp,
    searchClinicsInfoRespError,
    loadingSearchClinicsResp,
    searchClinicsRespError,
  } = useSearchClinics(groupMemberInfo?.UID);

  const { memberInfoResp, setMemberInfo, loadingMemberInfo, memberInfoError } =
    useSetMemberInfo(groupMemberInfo?.UID, memberGroups[0]?.groupId);
  const {
    setGroupMemberNickname,
    loadinggroupMemberNickname,
    groupMemberNicknameError,
  } = useSetGroupMemberInfo();
  const {
    setGroupInfoId,
    setRemovedUsers,
    loadingGroupInfoResp,
    groupInfoRespError,
  } = useSetGroupInfo();

  const [tabIndex, setTabIndex] = useState(0);
  const TEAM_DETAILS_TAB = 0;
  const MEMBERS_TAB = 1;

  const [isModalNicknameOpen, setIsModalNicknameOpen] = useState(false);
  const [isModalNewMemberOpen, setIsModalNewMemberOpen] = useState(false);
  const [isModalRemoveMemberOpen, setIsModalRemovedMemberOpen] =
    useState(false);
  const [isModalLeaveTeamOpen, setIsModalLeaveTeamOpen] = useState(false);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [searchTermClinics, setSearchTermClinics] = useState("");
  const [removedMember, setRemovedMember] = useState<
    GroupMemberInfo | undefined
  >(undefined);

  // GET SEARCHCLINICSINFO RESP
  useEffect(() => {
    if (groupMemberInfo) {
      setGroupId(groupMemberInfo.groupId);
    }
  }, [groupMemberInfo]);

  const appContainer = css`
    margin: 0 ${isMobile ? "45px" : "150px"};
  `;

  const flexRow = css`
    display: flex;
    justify-content: space-between;
    margin-top: ${isMobile ? "30px" : "80px"};
  `;

  const loadingDiv = css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
  `;

  const appHeaderStyles = css`
    font-family: Montserrat;
    font-size: 32px;
    font-style: normal;
    font-weight: 800;
    line-height: 38px;
  `;

  const appDescrStyles = css`
    font-family: Montserrat;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 31px;
  `;

  const cancelSaveRowStyles = css`
    display: flex;
    justify-content: flex-end;
  `;

  const detailsTabFlexContainer = css`
    display: flex;
    flex-direction: column;
  `;

  const membersTabFlexContainer = css`
    display: flex;
    flex-direction: column;
  `;

  const handleDialogClose = () => {
    setIsModalRemovedMemberOpen(false);
  };

  const handleModalLeaveTeamClose = () => {
    setIsModalLeaveTeamOpen(false);
  };

  const handleEditClick = () => {
    setIsModalNicknameOpen(true);
  };

  const handleTabIndexChange = (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setTabIndex(newValue);
  };

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // TODO
  };

  const handleSelectClose = () => {
    // TODO
  };

  const makeStrArr = (arg: string | string[] | undefined) => {
    if (arg === undefined) {
      return [];
    }

    if (typeof arg === "string") {
      return [arg];
    }

    if (Array.isArray(arg)) {
      return arg;
    }

    return [];
  };

  const handleDelete = (member: GroupMemberInfo | undefined) => {
    if (member) {
      // LOGIC FOR SETGROUPINFO
      // UPDATES THE REMOVEDUSERS propperty
      if (process.env.REACT_APP_LOCALE !== "en-us") {
        // removed members with searchclinicsinforesp
        setGroupInfoId(searchClinicsInfoResp?.groupId || "");
        if (searchClinicsInfoResp && searchClinicsInfoResp.groupData) {
          setRemovedUsers([
            ...new Set([
              ...makeStrArr(searchClinicsInfoResp.groupData.removedUsers),
              member.UID,
            ]),
          ]);
        } else {
          if (searchClinicsInfoResp) setRemovedUsers([member.UID]);
        }
      } else {
        // removed members with membergroups
        setGroupInfoId(memberGroups[selectedGroupIndex].groupId);
        if (memberGroups[selectedGroupIndex].groupData) {
          setRemovedUsers([
            ...new Set([
              ...makeStrArr(
                memberGroups[selectedGroupIndex].groupData.removedUsers,
              ),
              member.UID,
            ]),
          ]);
        } else {
          setRemovedUsers([member.UID]);
        }
      }

      const updatedResolvedMembers = resolvedMembers.filter(
        (resolvedMember) => resolvedMember.UID !== member.UID,
      );

      const newGroupId =
        process.env.REACT_APP_LOCALE === "en-us"
          ? memberGroups[selectedGroupIndex].groupId
          : searchClinicsResp[selectedGroupIndex].groupId;

      setResolvedMembers(updatedResolvedMembers);
      setIsModalRemovedMemberOpen(false);
      setRemovedMemberInfo({
        emailId: member.profile.email.toString(),
        groupId: newGroupId,
      });
    }
  };

  // SAME AS HANDLE DELETE BUT CLOSES THE LEAVE TEAM MODAL
  const handleLeave = (member: GroupMember | undefined) => {
    if (member) {
      // LOGIC FOR SETGROUPINFO
      // UPDATES THE REMOVEDUSERS propperty
      if (process.env.REACT_APP_LOCALE !== "en-us") {
        // removed members with searchclinicsinforesp
        setGroupInfoId(searchClinicsInfoResp?.groupId || "");
        if (searchClinicsInfoResp && searchClinicsInfoResp.groupData) {
          setRemovedUsers([
            ...new Set([
              ...makeStrArr(searchClinicsInfoResp.groupData.removedUsers),
              member.UID,
            ]),
          ]);
        } else {
          if (searchClinicsInfoResp) setRemovedUsers([member.UID]);
        }
      } else {
        // removed members with membergroups
        setGroupInfoId(memberGroups[selectedGroupIndex].groupId);
        if (memberGroups[selectedGroupIndex].groupData) {
          setRemovedUsers([
            ...new Set([
              ...makeStrArr(
                memberGroups[selectedGroupIndex].groupData.removedUsers,
              ),
              member.UID,
            ]),
          ]);
        } else {
          setRemovedUsers([member.UID]);
        }
      }

      const newGroupId =
        process.env.REACT_APP_LOCALE === "en-us"
          ? memberGroups[selectedGroupIndex].groupId
          : searchClinicsResp[selectedGroupIndex].groupId;

      setIsModalLeaveTeamOpen(false);
      setRemovedMemberInfo({
        emailId: "",
        groupId: newGroupId,
      });

      // Remove the group/clinic from the local state
      setSearchClinicsResp(
        searchClinicsResp.filter(
          (serchClinic) =>
            serchClinic.groupId !==
            searchClinicsResp[selectedGroupIndex].groupId,
        ),
      );
      setMemberGroups(
        memberGroups.filter(
          (memberGroup) =>
            memberGroup.groupId !== memberGroups[selectedGroupIndex].groupId,
        ),
      );
    } else {
      console.log("This user does not exist");
    }
  };

  const filterClinics = (clinic: GroupInfo<GigyaGroupModels.ClinicModel>) => {
    const dropDownValue =
      clinic.relationshipData.nickname ?? clinic.groupData.clinicName;
    return dropDownValue
      .toString()
      .toLowerCase()
      .includes(searchTermClinics.toLocaleLowerCase());
  };

  const filterClinicsTW = (clinic: SEARCHGROUP) => {
    const dropDownValue = clinic.groupId;
    return dropDownValue
      .toString()
      .toLowerCase()
      .includes(searchTermClinics.toLocaleLowerCase());
  };

  const getDropDownOptions = () => {
    if (process.env.REACT_APP_LOCALE !== "en-us") {
      // ASIA
      // SEARCHCLINICSRESP
      // SEARCHCLINICSINFORESP
      // THIS SORT MAY NEED TO BE APPLIED TO THE ALL HOOKS TO ENSURE
      // THAT THE DROP DOWNS LINE UP WITH ALL OTHER CLINIC DATA
      return searchClinicsResp
        .sort((clinicA, clinicB) => {
          return clinicA.memberSinceTimestamp - clinicB.memberSinceTimestamp;
        })
        ?.map((searchClinicResp, index) => {
          return { ...searchClinicResp, indexOrig: index };
        })
        ?.filter(filterClinicsTW)
        ?.map((c, i) => ({
          value: i,
          label: c.groupId,
          indexOrig: c.indexOrig,
        }));
    }
    // US
    // MEMBERGROUPS
    // GROUPMEMBERINFO
    return memberGroups
      .map((memberGroup, index) => {
        return { ...memberGroup, indexOrig: index };
      })
      .filter(filterClinics)
      .map((c, i) => ({
        value: i,
        label: (c.relationshipData.nickname ??
          c.groupData.clinicName) as string,
        indexOrig: c.indexOrig,
      }));
  };

  const handleSearchFilter = (
    searchEvt: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchTermClinics(searchEvt.target.value);
  };

  const handleDropDownChange = (index: unknown) => {
    if (process.env.REACT_APP_LOCALE !== "en-us") {
      const selectedGroupId = searchClinicsResp[Number(index)].groupId;
      setGroupId(selectedGroupId); // Get new info response of selected clinic. This gives us the clinic name
      setNewGroupMemberInfoID(searchClinicsResp[Number(index)].groupId); // Get new memberInfo response for nickname
      setSelectedGroupIndex(Number(index));
    } else {
      setNewGroupMemberInfoID(memberGroups[Number(index)].groupId); // Get new memberInfo response for nickname
      setSelectedGroupIndex(Number(index));
    }
  };

  const handleDisableTabMember = () => {
    if (process.env.REACT_APP_LOCALE !== "en-us" && searchClinicsResp.length) {
      return (
        searchClinicsResp[selectedGroupIndex].relationshipData.role ===
          "member" ||
        searchClinicsResp[selectedGroupIndex].relationshipData.status !==
          "approved"
      );
    }

    if (process.env.REACT_APP_LOCALE === "en-us" && memberGroups.length) {
      return memberGroups[selectedGroupIndex].relationshipData.role !== "admin";
    }

    return true;
  };

  const isClinicListEmpty = () => {
    if (
      searchClinicsResp.length === 0 &&
      process.env.REACT_APP_LOCALE !== "en-us"
    ) {
      return true;
    }

    if (memberGroups.length === 0 && process.env.REACT_APP_LOCALE === "en-us") {
      return true;
    }

    return false;
  };

  // The component is rendering the empty component because the state variables are
  // initially empty/undefined. In stead of using if statements, return all the
  // options in a single return but use the logical operators to handle what gets
  // rendered
  const LoadingDetails = () => {
    // US LOCALE
    if (process.env.REACT_APP_LOCALE === "en-us") {
      // CHECK ERROR STATES
      if (
        (groupMemberInfoError || memberGroupsError) &&
        tabIndex === TEAM_DETAILS_TAB
      ) {
        return (
          <div css={loadingDiv}>
            <ErrorIcon />
          </div>
        );
      }
      // CHECK LOADING STATES
      if (
        (loadingGroupMemberInfo || loadingMemberGroups) &&
        tabIndex === TEAM_DETAILS_TAB
      ) {
        return (
          <div css={loadingDiv}>
            <Loading />
          </div>
        );
      }
      // RENDER NORMAL STATE
      if (groupMemberInfo && tabIndex === TEAM_DETAILS_TAB) {
        return (
          <div css={detailsTabFlexContainer}>
            <TableMember
              myUID={groupMemberInfo?.UID || ""}
              groupMemberInfo={groupMemberInfo}
              memberGroups={memberGroups}
              searchClinicsInfo={searchClinicsInfoResp}
              searchClinics={searchClinicsResp}
              handleEditClick={handleEditClick}
              selectedGroupIndex={selectedGroupIndex}
            />
            <Button
              sx={{
                alignSelf: "flex-end",
              }}
              id="button-leave-team"
              variant="text"
              startIcon={t("exported.Leave This Team")}
              onClick={() => setIsModalLeaveTeamOpen(true)}
            />
          </div>
        );
      }
    }

    // FOREIGN LOCALES
    if (process.env.REACT_APP_LOCALE !== "en-us") {
      // CHECK ERROR STATES
      if (
        (groupMemberInfoError ||
          searchClinicsRespError ||
          searchClinicsInfoRespError) &&
        tabIndex === TEAM_DETAILS_TAB
      ) {
        return (
          <div css={loadingDiv}>
            <ErrorIcon />
          </div>
        );
      }
      // CHECK LOADING STATES
      if (
        (loadingGroupMemberInfo ||
          loadingSearchClinicsResp ||
          loadingSearchClinicsInfoResp) &&
        tabIndex === TEAM_DETAILS_TAB
      ) {
        return (
          <div css={loadingDiv}>
            <Loading />
          </div>
        );
      }
      // RENDER NORMAL STATE
      if (groupMemberInfo && tabIndex === TEAM_DETAILS_TAB) {
        return (
          <div css={detailsTabFlexContainer}>
            <TableMember
              myUID={groupMemberInfo?.UID || ""}
              groupMemberInfo={groupMemberInfo}
              memberGroups={memberGroups}
              searchClinicsInfo={searchClinicsInfoResp}
              searchClinics={searchClinicsResp}
              handleEditClick={handleEditClick}
              selectedGroupIndex={selectedGroupIndex}
            />
            <Button
              id="button-leave-team"
              variant="text"
              startIcon={"Leave This Team"}
              onClick={() => setIsModalLeaveTeamOpen(true)}
            />
          </div>
        );
      }
    }

    return <></>;
  };

  const LoadingMembers = () => {
    if (tabIndex === MEMBERS_TAB) {
      return (
        <div css={membersTabFlexContainer}>
          {process.env.REACT_APP_LOCALE !== "en-us" && (
            <>
              <h1 css={appHeaderStyles}>{t("exported.Pending Request")}</h1>
              <TableMembersPending
                myUID={groupMemberInfo?.UID || ""}
                groupID={searchClinicsResp[selectedGroupIndex].groupId}
                group={searchClinicsResp[selectedGroupIndex]}
              />
              <h1 css={appHeaderStyles}>{t("exported.Member Directory")}</h1>
              {process.env.REACT_APP_LOCALE === "en-uss" && (
                <div>
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: "4px",
                      height: "35px",
                      marginBottom: "22px",
                    }}
                    onClick={() => setIsModalNewMemberOpen(true)}
                  >
                    Add A New Member
                  </Button>
                </div>
              )}
              <TableMembers
                myUID={groupMemberInfo?.UID || ""}
                groupID={searchClinicsResp[selectedGroupIndex].groupId}
                group={searchClinicsInfoResp}
              />
            </>
          )}
          {process.env.REACT_APP_LOCALE === "en-us" && (
            <h1 css={appHeaderStyles}>{t("exported.Member Directory")}</h1>
          )}
          {process.env.REACT_APP_LOCALE === "en-uss" && (
            <div>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: "4px",
                  height: "35px",
                  marginBottom: "22px",
                }}
                onClick={() => setIsModalNewMemberOpen(true)}
              >
                Add A New Member
              </Button>
            </div>
          )}
          {process.env.REACT_APP_LOCALE === "en-us" && (
            <>
              <TableMembers
                myUID={groupMemberInfo?.UID || ""}
                groupID={memberGroups[selectedGroupIndex].groupId}
                group={memberGroups[selectedGroupIndex]}
              />
            </>
          )}
        </div>
      );
    }

    return <></>;
  };

  return (
    <UserContext.Provider value={{ memberGroups, groupMemberInfo }}>
      <div css={appContainer}>
        {/* <ModalRemoveMember
          member={removedMember}
          isOpen={isModalRemoveMemberOpen}
          close={handleDialogClose}
          deleteMember={() => handleDelete(removedMember)}
          groupId={groupMemberInfo?.groupId || ""}
        /> */}
        <ModalNickname
          isOpen={isModalNicknameOpen}
          close={() => setIsModalNicknameOpen(false)}
          save={(inputVal) => {
            if (groupMemberInfo && groupMemberInfo.UID) {
              const updatedGroupMemberInfo = {
                ...groupMemberInfo,
                relationshipData: {
                  ...groupMemberInfo?.relationshipData,
                  nickname: inputVal,
                },
              };

              // Updates the nickname of the group in the state array
              // being used for the dropdown.
              const updatedMembersArr = memberGroups.map((group) => {
                if (group.groupId === groupMemberInfo.groupId) {
                  return {
                    ...group,
                    relationshipData: {
                      ...group.relationshipData,
                      nickname: inputVal,
                    },
                  };
                }
                return group;
              });

              const updatedClinicsArr = searchClinicsResp.map((clinic) => {
                if (clinic.groupId === groupMemberInfo.groupId) {
                  return {
                    ...clinic,
                    relationshipData: {
                      ...clinic.relationshipData,
                      nickname: inputVal,
                    },
                  };
                }
                return clinic;
              });

              setSearchClinicsResp(updatedClinicsArr);
              setMemberGroups(updatedMembersArr);
              setGroupMemberInfo(updatedGroupMemberInfo);
            }

            setIsModalNicknameOpen(false);
            setGroupMemberNickname({
              nickname: inputVal,
              groupId: groupMemberInfo?.groupId,
            });
          }}
        />
        <ModalNewMember
          isOpen={isModalNewMemberOpen}
          close={() => setIsModalNewMemberOpen(false)}
          groupId={groupMemberInfo?.groupId || ""}
        />
        <ModalLeaveTeam
          member={groupMemberInfo}
          isOpen={isModalLeaveTeamOpen}
          close={handleModalLeaveTeamClose}
          deleteMember={() => {
            handleLeave(groupMemberInfo);
          }}
          groupId={groupMemberInfo?.groupId || ""}
        />

        {/* Render <Empty /> if not loading and important state values are empty */}
        {isClinicListEmpty() && <Empty />}
        {!isClinicListEmpty() && (
          <>
            <div css={flexRow}>
              <h1 css={appHeaderStyles}>{t("exported.Team Management")}</h1>
              <LanguageSelector />
            </div>
            <p css={appDescrStyles}>
              {t("exported.Select a team to access its details below.")}
            </p>
            <SelectW
              skeleton={!memberGroups || !searchClinicsResp}
              label={t("exported.Selected Team")}
              value={() => selectedGroupIndex ?? ""}
              items={getDropDownOptions()}
              onChange={handleDropDownChange}
              search
              searchLabel="Search Clinics"
              onSearchChange={handleSearchFilter}
              onClose={() => setSearchTermClinics("")}
            />
            <Tabs
              sx={{ marginTop: "22px", marginBottom: "22px" }}
              variant="fullWidth"
              value={tabIndex}
              onChange={handleTabIndexChange}
            >
              <Tab
                label={t("exported.Team Details")}
                {...a11yProps(TEAM_DETAILS_TAB)}
              />
              {process.env.REACT_APP_LOCALE === "en-us" &&
                handleDisableTabMember() === false && (
                  <Tab
                    label={t("exported.Members")}
                    {...a11yProps(MEMBERS_TAB)}
                    disabled={handleDisableTabMember()}
                  />
                )}
              {process.env.REACT_APP_LOCALE !== "en-us" && (
                <Tab
                  label={t("exported.Members")}
                  {...a11yProps(MEMBERS_TAB)}
                  disabled={handleDisableTabMember()}
                />
              )}
            </Tabs>

            {/* DETAILS TAB STARTS HERE */}
            <LoadingDetails />
            {/* DETAILS TAB ENDS HERE */}

            {/* MEMBERS TAB STARTS HERE */}
            <LoadingMembers />
            {/* MEMBERS TAB ENDS HERE */}
          </>
        )}
      </div>
    </UserContext.Provider>
  );
}
